import {
  DeliverableSetStatus,
  RequestApprovalUserInvitation,
  ResponseDeliverableSet,
  User,
} from '@/schema'

import { ERoleType } from '../../user-management'
import { IInviteUserDeliverableSet } from '../types'

/**
 * Build approval user invitation
 * @param inviteUsers - The invite users
 * @returns The approval user invitation
 */
export const buildApprovalUserInvitation = (
  inviteUsers: IInviteUserDeliverableSet[],
  deliverableSetId: string
): RequestApprovalUserInvitation[] => {
  // If no deliverable set id or no invite users, return empty array
  if (!deliverableSetId || !inviteUsers?.length) return []

  return inviteUsers.map(
    (user) =>
      ({
        email: user.email,
        first_name: user.firstName,
        last_name: user.lastName,
        id: user.id,
        role_id: user.roleId,
        role_name: user.roleName,
        scopes: [
          {
            resource_id: deliverableSetId,
            resource_type: 'deliverable_set',
          },
        ],
      }) as RequestApprovalUserInvitation
  )
}

/**
 * Build invite user deliverable set
 * @param users - The users
 * @returns The invite user deliverable set
 */
export const buildInviteUserDeliverableSet = (
  users: RequestApprovalUserInvitation[]
): IInviteUserDeliverableSet[] => {
  // If no users, return empty array
  if (!users?.length) return []

  return users.map(
    (user) =>
      ({
        id: user.id,
        uuid: user.id ? undefined : crypto.randomUUID(),
        firstName: user.first_name,
        lastName: user.last_name,
        email: user.email,
        roleId: user.role_id,
        roleName: user.role_name,
      }) as IInviteUserDeliverableSet
  )
}

/**
 * Get reviewer users
 * @param deliverableSet - The deliverable set
 * @returns The reviewer users
 */
export const getReviewerUsers = (
  deliverableSet: ResponseDeliverableSet
): { users: User[]; greyedOut: boolean } => {
  let users: User[] = deliverableSet.reviewers ?? []
  let greyedOut = false

  if (!deliverableSet) return { users: [], greyedOut: false }

  if (deliverableSet.status === DeliverableSetStatus.DRAFT) {
    if (deliverableSet.draft_approval?.reviewers) {
      users = deliverableSet.draft_approval?.reviewers.filter(
        (r) => r.role_name === ERoleType.REVIEWER
      ) as User[]
      greyedOut = !!users.length
    }
  }

  return { users, greyedOut }
}

/**
 * Get viewer users
 * @param deliverableSet - The deliverable set
 * @returns The viewer users
 */
export const getViewerUsers = (
  deliverableSet: ResponseDeliverableSet
): { users: User[]; greyedOut: boolean } => {
  let users: User[] = deliverableSet.viewers ?? []
  let greyedOut = false

  if (!deliverableSet) return { users: [], greyedOut: false }

  if (deliverableSet.status === DeliverableSetStatus.DRAFT) {
    if (deliverableSet.draft_approval?.reviewers) {
      users = deliverableSet.draft_approval?.reviewers.filter(
        (r) => r.role_name === ERoleType.VIEWER
      ) as User[]
      greyedOut = !!users.length
    }
  }

  return { users, greyedOut }
}

/**
 * Check if the user is assigned in the project
 * @param role - The role
 * @param projectId - The project id
 * @returns True if the user is assigned in the project, false otherwise
 */
export const isAssignedInProject = (
  role: NonNullable<User['roles']>[number],
  projectId?: string | null
): boolean => {
  if (!projectId) return false

  return !!(
    [ERoleType.PROJECT_LEAD, ERoleType.TEAM_MEMBER].includes(
      role.role_name as ERoleType
    ) && role.scopes?.some((scope) => scope.project_id === projectId)
  )
}
